import { ref, readonly } from 'vue'
import { get, set, del, keys } from 'idb-keyval'
import { useNetworkStatus } from '@/composables/useNetworkStatus'

/**
 * useOfflineQueue — persistent FIFO mutation queue.
 *
 * Mutations the ky client cannot send (because the network is down or the
 * request failed transiently) are stashed in IndexedDB along with all headers
 * — crucially including any Idempotency-Key. On reconnect the queue replays
 * in insertion order. Backend idempotency ensures retried mutations don't
 * double-fire even if the user also manually retried while offline.
 *
 * Scope: write methods only (POST/PUT/PATCH/DELETE). GETs are not queued —
 * if a read fails offline the caller should fall back to cached data.
 */

export interface QueuedMutation {
  id: string                  // crypto.randomUUID — used as IDB key prefix
  url: string
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  headers: Record<string, string>
  body: string | null
  enqueuedAt: number
  attempts: number
  lastError?: string
}

const KEY_PREFIX = 'hudstore:offline-queue:'
const pendingCount = ref(0)
const isFlushing = ref(false)

async function listMutationKeys(): Promise<string[]> {
  const all = await keys()
  return all
    .filter((k): k is string => typeof k === 'string' && k.startsWith(KEY_PREFIX))
    .sort()  // FIFO order — keys embed enqueuedAt timestamp
}

async function refreshCount() {
  const k = await listMutationKeys()
  pendingCount.value = k.length
}

export async function enqueueMutation(
  url: string,
  method: QueuedMutation['method'],
  headers: Record<string, string>,
  body: string | null
): Promise<QueuedMutation> {
  const id = crypto.randomUUID()
  const mutation: QueuedMutation = {
    id,
    url,
    method,
    headers,
    body,
    enqueuedAt: Date.now(),
    attempts: 0
  }
  // Key embeds timestamp so iteration order is naturally FIFO.
  const key = `${KEY_PREFIX}${mutation.enqueuedAt.toString().padStart(16, '0')}:${id}`
  await set(key, mutation)
  await refreshCount()
  return mutation
}

export async function dequeueMutation(key: string): Promise<void> {
  await del(key)
  await refreshCount()
}

export async function flushQueue(): Promise<{ ok: number; failed: number }> {
  if (isFlushing.value) return { ok: 0, failed: 0 }
  isFlushing.value = true
  let ok = 0
  let failed = 0
  try {
    const allKeys = await listMutationKeys()
    for (const key of allKeys) {
      const mutation = await get<QueuedMutation>(key)
      if (!mutation) continue
      try {
        const res = await fetch(mutation.url, {
          method: mutation.method,
          headers: mutation.headers,
          body: mutation.body,
          credentials: 'include'
        })
        if (res.ok || res.status === 409) {
          // 409 (idempotency replay) is success — the original write committed.
          await dequeueMutation(key)
          ok++
        } else if (res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429) {
          // Client error that won't fix itself — drop to avoid replay loops.
          await dequeueMutation(key)
          failed++
        } else {
          // 5xx / network → keep for next flush, increment attempts.
          mutation.attempts += 1
          mutation.lastError = `HTTP ${res.status}`
          await set(key, mutation)
          failed++
        }
      } catch (e) {
        // Network still flaky — leave queued, try next flush.
        mutation.attempts += 1
        mutation.lastError = e instanceof Error ? e.message : 'fetch failed'
        await set(key, mutation)
        failed++
      }
    }
  } finally {
    isFlushing.value = false
  }
  return { ok, failed }
}

export async function purgeQueue(): Promise<void> {
  const allKeys = await listMutationKeys()
  for (const key of allKeys) await del(key)
  await refreshCount()
}

export async function listQueue(): Promise<QueuedMutation[]> {
  const allKeys = await listMutationKeys()
  const out: QueuedMutation[] = []
  for (const k of allKeys) {
    const m = await get<QueuedMutation>(k)
    if (m) out.push(m)
  }
  return out
}

// Auto-flush wiring: registered once on first composable use. Reconnects
// trigger a flush; we also kick one on import in case the page loaded while
// already online with leftover entries from a prior session.
let autoFlushInstalled = false
function installAutoFlush() {
  if (autoFlushInstalled) return
  autoFlushInstalled = true
  refreshCount()
  if (typeof window !== 'undefined' && navigator.onLine) {
    flushQueue().catch(() => { /* swallow startup flush errors */ })
  }
}

export function useOfflineQueue() {
  installAutoFlush()
  const { onReconnect } = useNetworkStatus()
  onReconnect(() => {
    flushQueue().catch(() => { /* logged via toast in caller if needed */ })
  })
  return {
    pendingCount: readonly(pendingCount),
    isFlushing: readonly(isFlushing),
    enqueueMutation,
    flushQueue,
    purgeQueue,
    listQueue
  }
}

// Custom error type ky callers can recognize when a mutation is queued instead
// of sent. Distinguishable from a real network failure.
export class OfflineQueuedError extends Error {
  constructor(public mutation: QueuedMutation) {
    super(`Mutation queued for replay when connection returns (id=${mutation.id})`)
    this.name = 'OfflineQueuedError'
  }
}
